import type { NavItemData, NavSectionData } from '@layouts/dashboard/types';

import { NavLink, useLocation } from 'react-router';

import { cn } from '@shared/utils/cn';

const isActive = (pathname: string, { path, deepMatch }: NavItemData) =>
  deepMatch ? pathname === path || pathname.startsWith(`${path}/`) : pathname === path;

const NavItem = ({ item }: { item: NavItemData }) => {
  const { pathname } = useLocation();
  const active = isActive(pathname, item);

  return (
    <NavLink
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex min-h-11 items-center gap-4 rounded-lg px-3 py-2 text-sm transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50',
        active ? 'bg-accent font-semibold text-foreground' : 'font-medium text-muted-foreground hover:text-foreground'
      )}
      data-slot="nav-item"
      end
      to={item.path}
    >
      {active && <span aria-hidden className="absolute top-2 bottom-2 -left-1 w-1 rounded-full bg-primary" />}
      {item.icon && (
        <span
          className={cn(
            'flex size-6 shrink-0 items-center justify-center [&>svg]:size-5',
            active ? 'text-primary' : 'text-muted-foreground/80'
          )}
        >
          {item.icon}
        </span>
      )}
      <span className="flex min-w-0 grow flex-col">
        <span className="truncate">{item.title}</span>
        {item.caption && <span className="truncate text-xs font-normal text-muted-foreground/60">{item.caption}</span>}
      </span>
      {item.info}
    </NavLink>
  );
};

type Props = {
  data: NavSectionData[];
};

/** Sections of the side menu: optional subheader + list of links. */
export const NavSections = ({ data }: Props) => (
  <nav className="flex flex-1 flex-col gap-1 px-4 pb-6" data-slot="nav-sections">
    {data.map((section, index) => (
      <div className="flex flex-col gap-0.5" key={section.subheader ?? index}>
        {section.subheader && (
          <span className="px-3 pt-4 pb-2 text-[11px] font-semibold tracking-wide text-muted-foreground/60 uppercase">
            {section.subheader}
          </span>
        )}
        {section.items.map((item) => (
          <NavItem item={item} key={item.path} />
        ))}
      </div>
    ))}
  </nav>
);
